import React, {useCallback, useMemo, useRef, useState} from 'react';
import DataGrid, {
  Button as GridButton,
  Column,
  ColumnFixing,
  FilterRow,
  Paging,
  RemoteOperations,
  Scrolling,
  Editing,
  Lookup,
  Toolbar,
  Item
} from 'devextreme-react/data-grid';
import {Popover} from 'devextreme-react/popover';
import Button from 'devextreme-react/button';
import {paymentSystemOptions} from 'utils/variables';
import {getPsTypeStore} from '../../api/lookups';
import './payment-systems-conf.scss';
import {getDataSource} from '../../api/dataSource';
import IdFormatContent from 'components/common/IdFormatContent';
import {Popup} from 'devextreme-react';
import PaymentSystemParams from './payment-system-params';

const GRID_COLUMNS_LS_KEY = 'grid:payment-systems-conf:columns:v1';

const DEFAULT_COLUMN_WIDTHS = {
  ID: 150,
  Name: 300,
  PaymentSystemTypeID: 300,
  Status: 150,
};

const loadColumnWidths = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_COLUMN_WIDTHS;
    const raw = window.localStorage.getItem(GRID_COLUMNS_LS_KEY);
    if (!raw) return DEFAULT_COLUMN_WIDTHS;

    const parsed = JSON.parse(raw);
    const storedWidths = parsed && typeof parsed === 'object' ? parsed.widths : null;
    if (!storedWidths || typeof storedWidths !== 'object') return DEFAULT_COLUMN_WIDTHS;

    const allowedKeys = new Set(Object.keys(DEFAULT_COLUMN_WIDTHS));
    const sanitized = {};

    Object.keys(storedWidths).forEach((key) => {
      if (!allowedKeys.has(key)) return;
      const width = storedWidths[key];
      if (typeof width !== 'number' || !Number.isFinite(width)) return;
      if (width < 60 || width > 1000) return;
      sanitized[key] = width;
    });

    const merged = {...DEFAULT_COLUMN_WIDTHS, ...sanitized};

    const hadUnknownKeys = Object.keys(storedWidths).some((key) => !allowedKeys.has(key));
    if (hadUnknownKeys) {
      window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths: sanitized}));
    }

    return merged;
  } catch (e) {
    return DEFAULT_COLUMN_WIDTHS;
  }
};

const saveColumnWidths = (widths) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths}));
  } catch (e) {
    // ignore
  }
};

const statusData = [
  {ID: 0, Name: 'Disabled'},
  {ID: 1, Name: 'Active'}
];

const PaymentSystemsConf = (props) => {
  const gridRef = useRef(null);
  const [editingPopupTitle, setEditingPopupTitle] = useState('Редактирование платежной системы');
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);

  const haveEnoughRight = useMemo(() => {
    const {userRights} = props;
    return userRights.includes('11');
  }, []);

  // Main Data Source
  const dataSource = useMemo(() => {
    return getDataSource(paymentSystemOptions);
  }, []);

  // Shared store
  const psTypeStore = useMemo(() => getPsTypeStore(), []);

  const refreshGrid = () => {
    if (gridRef.current) {
      gridRef.current.instance().refresh();
    }
  };

  const onEditorPreparing = (e) => {
    // Make ID and PaymentSystemTypeID readonly during Edit
    if (e.parentType === 'dataRow') {
      const isEdit = !e.row.isNewRow;
      if (isEdit && (e.dataField === 'ID' || e.dataField === 'PaymentSystemTypeID')) {
        e.editorOptions.disabled = true;
      }
    }
  };

  const onGridOptionChanged = useCallback(
    (e) => {
      if (!e?.fullName || typeof e.fullName !== 'string') return;
      if (!e.fullName.endsWith('.width')) return;
      if (!e.fullName.startsWith('columns[')) return;
      if (!e.component) return;

      const nextWidths = {};
      Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
        const width = e.component.columnOption(dataField, 'width');
        if (typeof width === 'number' && Number.isFinite(width)) {
          nextWidths[dataField] = width;
        }
      });

      if (!Object.keys(nextWidths).length) return;

      setColumnWidths((prev) => {
        let changed = false;
        Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
          if (prev[dataField] !== nextWidths[dataField]) changed = true;
        });
        if (!changed) return prev;

        const merged = {...prev, ...nextWidths};
        saveColumnWidths(merged);
        return merged;
      });
    },
    [setColumnWidths],
  );

  const resetColumns = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(GRID_COLUMNS_LS_KEY);
      }
    } catch (e) {
      // ignore
    }
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
  };

  const [isVisibleParamsForm, setIsVisibleParamsForm] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(null);

  const closeParamsForm = () => {
    setIsVisibleParamsForm(false);
    setSelectedSystem(null);
  };

  const openParamsForm = (paymentSystem) => {
    setSelectedSystem(paymentSystem);
    setIsVisibleParamsForm(true);
  };

  return (
    <div>
      <h4 className={'content-block grid-title'}>Платежные системы (Конф.)</h4>
      <Popup
        onHiding={closeParamsForm}
        hideOnOutsideClick={false}
        visible={isVisibleParamsForm}
        container={'#main-content'}
        contentRender={() =>
          selectedSystem ? (
            <PaymentSystemParams
              paymentSystem={selectedSystem}
              userRights={props.userRights}
              onClose={closeParamsForm}
            />
          ) : null
        }
        showCloseButton={true}
        title={selectedSystem ? `Параметры платежной системы (Конф.) ${selectedSystem.ID} | ${selectedSystem.Name}` : ''}
      />
      <div className={'content-block grid-wrapper payment-systems-conf-wrapper'}>
        <DataGrid
          ref={gridRef}
          dataSource={dataSource}
          keyExpr={'ID'}
          hoverStateEnabled={true}
          height={'100%'}
          width={'100%'}
          wordWrapEnabled={true}
          onEditorPreparing={onEditorPreparing}
          allowColumnResizing={true}
          columnResizingMode={'widget'}
          columnAutoWidth={false}
          onOptionChanged={onGridOptionChanged}
          onEditingStart={() => setEditingPopupTitle('Редактирование платежной системы')}
          onInitNewRow={() => setEditingPopupTitle('Новая платежная система')}
        >
          <Toolbar>
            <Item location={'before'}>
              <Button text={'Обновить'} onClick={refreshGrid}/>
            </Item>
            <Item location={'before'}>
              <Button
                text={'Сбросить фильтр'}
                onClick={() => {
                  if (gridRef.current) {
                    gridRef.current.instance().clearFilter();
                  }
                }}
              />
            </Item>
            <Item location={'before'}>
              <Button text={'Сбросить ширины'} onClick={resetColumns}/>
            </Item>
            <Item location="after">
              <Button
                visible={haveEnoughRight}
                text="Добавить"
                icon="add"
                onClick={() => {
                  if (gridRef.current) {
                    setEditingPopupTitle('Новая платежная система');
                    gridRef.current.instance().addRow();
                  }
                }}
              />
            </Item>
          </Toolbar>

          <RemoteOperations filtering={false} paging={false} sorting={false}/>
          <Paging enabled={false}/>
          <Scrolling mode={'infinite'} showScrollbar={'always'}/>
          <ColumnFixing enabled={true}/>
          <FilterRow visible={true}/>

          <Editing
            mode="popup"
            allowAdding={true}
            allowUpdating={true}
            allowDeleting={true}
            useIcons={true}
            popup={{
              title: editingPopupTitle,
              showTitle: true,
              width: 500,
              height: 350,
            }}
            form={{
              colCount: 1,
              labelLocation: 'left',
              items: [
                {
                  dataField: 'ID',
                  editorOptions: {
                    placeholder: 'Формат [PS Type][Merchant ID][Config No] (напр. 3400241)',
                    buttons: [{
                      name: 'hint',
                      location: 'after',
                      options: {
                        icon: 'help',
                        stylingMode: 'text',
                        elementAttr: {id: 'id-info-hint'},
                        hoverStateEnabled: false,
                        activeStateEnabled: false,
                        focusStateEnabled: false
                      }
                    }]
                  }
                },
                'Name',
                'PaymentSystemTypeID',
                'Status'
              ]
            }}
          />

          <Column dataField="ID" caption="ID" dataType="number" width={columnWidths.ID}/>
          <Column dataField="Name" caption="Название" width={columnWidths.Name}/>

          <Column dataField="PaymentSystemTypeID" caption="Тип ПС" width={columnWidths.PaymentSystemTypeID}>
            <Lookup dataSource={psTypeStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column dataField="Status" caption="Статус" dataType="number" width={columnWidths.Status}>
            <Lookup dataSource={statusData} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column type="buttons" fixed={true} fixedPosition="right" width={130}>
            <GridButton visible={haveEnoughRight} name="edit"/>
            <GridButton visible={haveEnoughRight} name="delete"/>
            <GridButton
              visible={haveEnoughRight}
              hint="Params"
              icon="preferences"
              onClick={(e) => {
                openParamsForm(e.row.data);
              }}
            />
          </Column>

        </DataGrid>
      </div>
      <Popover
        target="#id-info-hint"
        showEvent="mouseenter"
        hideEvent="mouseleave"
        position="bottom"
        width={400}
        shading={false}
        contentRender={() => <IdFormatContent/>}
      />
    </div>
  );
};

export default PaymentSystemsConf;
